// ── onesdk_utility section (mockup) ─────────────────────────────────────────
//
// Binds OneSDK flow IDs to v2 workflows (the flow → workflow map you set by
// hand today), plus which OCR-extracted fields appear on the review screen.
// Local state only — not saved to the workflow JSON.
import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Checkbox, Field, TextInput } from './fields'
import { ConfigSection } from './ConfigSection'

interface MapRow {
  flowId: string
  workflow: string
}

const REVIEW_FIELDS = ['name', 'address', 'dob', 'documents'] as const
type ReviewField = (typeof REVIEW_FIELDS)[number]

export function OnesdkUtilitySection() {
  const [rows, setRows] = useState<MapRow[]>([{ flowId: 'idv', workflow: '' }])
  const [fields, setFields] = useState<Record<ReviewField, boolean>>({
    name: true,
    address: true,
    dob: true,
    documents: true,
  })

  const setRow = (i: number, p: Partial<MapRow>) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...p } : x)))
  const addRow = () => setRows((r) => [...r, { flowId: '', workflow: '' }])
  const removeRow = (i: number) => setRows((r) => r.filter((_, j) => j !== i))
  const toggleField = (k: ReviewField) => setFields((f) => ({ ...f, [k]: !f[k] }))

  return (
    <ConfigSection title="Flow → workflow map" file="onesdk_utility">
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[10px] uppercase tracking-wide text-[#5b6b85]">Workflow map</p>
          <button
            type="button"
            onClick={addRow}
            className="text-[10px] flex items-center gap-1 text-[#818cf8] hover:text-[#a5b4fc]"
          >
            <Plus size={11} />
            Add mapping
          </button>
        </div>
        <div className="flex flex-col gap-1.5">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <TextInput value={r.flowId} onChange={(e) => setRow(i, { flowId: e.target.value })} placeholder="flow id" />
              <span className="text-[#5b6b85] text-xs shrink-0">→</span>
              <TextInput value={r.workflow} onChange={(e) => setRow(i, { workflow: e.target.value })} placeholder="workflow" />
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="p-1 text-[#5b6b85] hover:text-[#fb7185] shrink-0"
                title="Remove mapping"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <Field label="OCR review fields">
        <div className="flex flex-col gap-1">
          {REVIEW_FIELDS.map((k) => (
            <Checkbox key={k} label={k} checked={fields[k]} onChange={() => toggleField(k)} />
          ))}
        </div>
      </Field>
    </ConfigSection>
  )
}
